import type { ReactNode } from 'react';
import { Database, PlugZap } from 'lucide-react';
import { Button } from './button';
import { Card } from './card';
import { EmptyState } from './empty-state';

export interface NotConnectedProps {
  /** Human name of the source, e.g. "Internal eZhire DB" or "Firebase Analytics". */
  sourceLabel: string;
  /** Short description of what the page would show once connected. */
  body?: ReactNode;
  /** Click handler for the CTA. Callers wire their own router. */
  onConnect?: () => void;
  ctaLabel?: string;
  /** If true, renders without a surrounding Card — for inline use. */
  inline?: boolean;
  /** If true, uses the PlugZap icon instead of the generic Database. */
  usePlugIcon?: boolean;
  className?: string;
}

/**
 * Generic "Not connected" empty state. Pairs with
 * `<DataRequirementsPanel />` — the panel tells you WHAT isn't
 * connected, this renders the page body when the page can't show
 * meaningful content until it is.
 *
 * Intentionally *never* renders mock numbers. If you find yourself
 * about to render placeholder metrics so the page "looks populated",
 * render this instead.
 */
export function NotConnected({
  sourceLabel,
  body,
  onConnect,
  ctaLabel,
  inline = false,
  usePlugIcon = false,
  className,
}: NotConnectedProps) {
  const Icon = usePlugIcon ? PlugZap : Database;
  const cta = ctaLabel ?? 'Wire this source';

  const content = (
    <EmptyState
      icon={Icon}
      title={`${sourceLabel} not connected`}
      description={
        body ??
        `This page will populate once ${sourceLabel} is connected. Until then, the layout is live but no metrics are shown — by design, we never display sample or placeholder numbers.`
      }
      action={
        onConnect ? (
          <Button type="button" onClick={onConnect}>
            {cta}
          </Button>
        ) : null
      }
      className={className}
    />
  );

  if (inline) return content;
  return <Card className="p-8">{content}</Card>;
}
